import { useMutation, useQueryClient } from "@tanstack/react-query";

import { errorHandler } from "#app/monitoring/errorHandler";
import { showErrorToast } from "#app/notifications";
import {
  clusterQuery,
  getClusterQueryData,
  setClusterQueryData,
} from "#modules/cluster/infra/cluster.query";
import { ClusterDto } from "#modules/cluster/infra/fetchCluster.api";
import { QuoteRequestLineDto } from "#modules/quoteRequestLines/infra/fetchQuoteRequestLine.api";
import {
  getQuoteRequestLineQueryData,
  quoteRequestLineQuery,
  setQuoteRequestLineQueryData,
} from "#modules/quoteRequestLines/infra/quoteRequestLine.query";

import { updateExcludedProcessSteps } from "./updateExcludedProcessSteps.api";

export const useUpdateExcludedProcessSteps = ({
  sourceQuoteRequestLineId,
  clusterId,
}: {
  sourceQuoteRequestLineId: string;
  clusterId: string | undefined;
}) => {
  const queryClient = useQueryClient();

  const { mutate, ...rest } = useMutation({
    mutationFn: updateExcludedProcessSteps,

    onMutate: async ({ excludedProcessSteps: newExcludedProcessSteps }) => {
      const beforeMutateQuoteRequestLine: QuoteRequestLineDto | undefined =
        getQuoteRequestLineQueryData(queryClient, sourceQuoteRequestLineId);

      if (beforeMutateQuoteRequestLine?.cluster) {
        setQuoteRequestLineQueryData(queryClient, sourceQuoteRequestLineId, {
          ...beforeMutateQuoteRequestLine,
          cluster: {
            ...beforeMutateQuoteRequestLine.cluster,
            excludedProcessSteps: newExcludedProcessSteps,
          },
        });
      }

      const beforeMutateCluster: ClusterDto | null | undefined = clusterId
        ? getClusterQueryData(queryClient, clusterId)
        : undefined;

      if (clusterId && beforeMutateCluster) {
        setClusterQueryData(queryClient, clusterId, {
          ...beforeMutateCluster,
          excludedProcessSteps: newExcludedProcessSteps,
        });
      }

      return { beforeMutateQuoteRequestLine, beforeMutateCluster };
    },

    onError: (err, _, context) => {
      setQuoteRequestLineQueryData(
        queryClient,
        sourceQuoteRequestLineId,
        context?.beforeMutateQuoteRequestLine,
      );

      if (clusterId) {
        setClusterQueryData(queryClient, clusterId, context?.beforeMutateCluster);
      }

      showErrorToast({
        title: "Une erreur est survenue lors de la mise à jour des procédés",
      });

      errorHandler(err);
    },

    onSettled: async () => {
      void queryClient.invalidateQueries({
        queryKey: quoteRequestLineQuery({ id: sourceQuoteRequestLineId }).queryKey,
      });
      if (clusterId) {
        void queryClient.invalidateQueries({
          queryKey: clusterQuery(clusterId).queryKey,
        });
      }
    },
  });

  return { updateExcludedProcessSteps: mutate, ...rest };
};
